/**
 * FleetVehicleDetail — Section 4 (iter-135) Consolidated Vehicle Hub.
 *
 * Rendered INLINE inside the expanded row of the Fleet Status tab on
 * StaffDashboard. Replaces the standalone MyAssignedVehicleCard so that
 * every vehicle-related action lives inside one tab (STANDING RULE —
 * UI INTEGRATION).
 *
 * Panels rendered:
 *   • Full vehicle record (VIN, mileage, gas card assignment, notes)
 *   • Maintenance flag status + service-receipt upload
 *   • Daily inspection form (own vehicle · operation days only)
 *   • Fuel receipt submission (own vehicle · with pre-filled gas card)
 *   • Temporary assignment panel (Regional Managers only)
 *   • Inline monthly MPG trend chart
 *
 * The inspection + fuel receipt forms are shown ONLY when the current
 * user is the assigned (or active temp) driver of the vehicle. The
 * temp-assign form is shown ONLY to Regional Managers (role='manager').
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  AlertTriangle, ClipboardCheck, Upload, CheckCircle2, Fuel, UserPlus, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import VehicleMpgChart from './VehicleMpgChart';

const API = process.env.REACT_APP_BACKEND_URL;
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function FleetVehicleDetail({ vehicle, user, onChanged }) {
  const [mine, setMine] = useState(null); // { assigned, vehicle, is_operation_day, gas_card, recent_inspections }
  const [replacements, setReplacements] = useState([]);
  const [inspection, setInspection] = useState({ mileage: '', notes: '' });
  const [receipt, setReceipt] = useState({ url: '', amount: '', note: '' });
  const [fuel, setFuel] = useState({ date: todayISO(), station: '', gallons: '', total_amount: '', receipt_url: '' });
  const [tempForm, setTempForm] = useState({ user_id: '', days: 7, start_date: todayISO() });
  const [busy, setBusy] = useState(false);

  const role = (user?.role || '').toLowerCase();
  const isRM = role === 'manager';
  const isMine = useMemo(() => {
    const uid = user?.user_id;
    if (!uid) return false;
    if (vehicle?.assignment?.user_id === uid) return true;
    const ta = vehicle?.temp_assignment;
    if (ta?.active && ta?.user_id === uid) {
      const today = todayISO();
      return ta.start_date <= today && ta.end_date >= today;
    }
    return false;
  }, [vehicle, user]);

  const loadMine = useCallback(async () => {
    if (!isMine) return;
    try {
      const r = await fetch(`${API}/api/procurement/vehicles/mine`, { credentials: 'include' });
      if (r.ok) setMine(await r.json());
    } catch (_) { /* noop */ }
  }, [isMine]);

  const loadReplacements = useCallback(async () => {
    if (!isRM) return;
    try {
      // Reuse the org users endpoint. We filter client-side to Supervisor / Crew Leader with a license.
      const r = await fetch(`${API}/api/users?role=supervisor`, { credentials: 'include' });
      const r2 = await fetch(`${API}/api/users?role=crew_leader`, { credentials: 'include' });
      const a = r.ok ? await r.json() : [];
      const b = r2.ok ? await r2.json() : [];
      const merged = [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])]
        .filter((u) => u.drivers_license_number || u.has_drivers_license);
      setReplacements(merged);
    } catch (_) { /* noop */ }
  }, [isRM]);

  useEffect(() => { loadMine(); loadReplacements(); }, [loadMine, loadReplacements]);

  const onFile = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error('Max 4MB');
    const reader = new FileReader();
    reader.onload = () => setter(String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  const submitInspection = async () => {
    const m = Number(inspection.mileage);
    if (!m || m < 0) return toast.error('Enter a valid mileage');
    setBusy(true);
    const r = await fetch(`${API}/api/procurement/vehicles/mine/inspection`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mileage_reported: m, condition_notes: inspection.notes }),
    });
    setBusy(false);
    if (r.ok) {
      const j = await r.json();
      toast.success(j.maintenance_triggered ? 'Inspection filed · Maintenance flag triggered' : 'Inspection filed');
      setInspection({ mileage: '', notes: '' });
      loadMine(); onChanged?.();
    } else {
      const j = await r.json().catch(() => ({}));
      toast.error(j.detail || 'Failed to file inspection');
    }
  };

  const submitReceipt = async () => {
    if (!receipt.url) return toast.error('Attach a service receipt');
    setBusy(true);
    const r = await fetch(`${API}/api/procurement/vehicles/mine/receipt`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        receipt_url: receipt.url,
        receipt_amount: Number(receipt.amount) || null,
        receipt_note: receipt.note,
      }),
    });
    setBusy(false);
    if (r.ok) {
      toast.success('Receipt filed · flag cleared · broker & Finance notified');
      setReceipt({ url: '', amount: '', note: '' });
      loadMine(); onChanged?.();
    } else {
      const j = await r.json().catch(() => ({}));
      toast.error(j.detail || 'Failed to file receipt');
    }
  };

  const submitFuel = async () => {
    if (!fuel.station || !fuel.gallons || !fuel.total_amount || !fuel.receipt_url) {
      return toast.error('Fill all fields and attach the receipt');
    }
    setBusy(true);
    const r = await fetch(`${API}/api/procurement/vehicles/mine/fuel-receipt`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        date: fuel.date, station: fuel.station,
        gallons: Number(fuel.gallons), total_amount: Number(fuel.total_amount),
        receipt_url: fuel.receipt_url,
      }),
    });
    setBusy(false);
    if (r.ok) {
      toast.success('Fuel receipt filed · broker & Finance notified');
      setFuel({ date: todayISO(), station: '', gallons: '', total_amount: '', receipt_url: '' });
    } else {
      const j = await r.json().catch(() => ({}));
      toast.error(j.detail || 'Failed to file fuel receipt');
    }
  };

  const submitTempAssign = async () => {
    if (!tempForm.user_id) return toast.error('Pick a replacement');
    setBusy(true);
    const r = await fetch(`${API}/api/procurement/vehicles/${vehicle.vehicle_id}/temp-assign`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: tempForm.user_id,
        start_date: tempForm.start_date,
        days: Math.min(7, Math.max(1, Number(tempForm.days) || 7)),
      }),
    });
    setBusy(false);
    if (r.ok) {
      toast.success('Temporary assignment created');
      setTempForm({ user_id: '', days: 7, start_date: todayISO() });
      onChanged?.();
    } else {
      const j = await r.json().catch(() => ({}));
      toast.error(j.detail || 'Failed to create temporary assignment');
    }
  };

  const terminateTempAssign = async () => {
    setBusy(true);
    const r = await fetch(`${API}/api/procurement/vehicles/${vehicle.vehicle_id}/temp-terminate`, {
      method: 'POST', credentials: 'include',
    });
    setBusy(false);
    if (r.ok) {
      toast.success('Temporary assignment terminated');
      onChanged?.();
    } else {
      const j = await r.json().catch(() => ({}));
      toast.error(j.detail || 'Failed to terminate temporary assignment');
    }
  };

  const v = vehicle;
  const flag = v.maintenance_flag || {};
  const nextMarker = ((Math.floor((v.mileage_at_last_service || v.current_mileage || 0) / 5000)) + 1) * 5000;
  const isOpDay = mine?.is_operation_day;
  const gasCard = mine?.gas_card;
  const tempActive = v.temp_assignment?.active;

  return (
    <div className="mt-3 pt-3 border-t border-zinc-700 space-y-4" data-testid={`fleet-detail-${v.vehicle_id}`}>
      {/* Full record grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        {v.vehicle_code && (
          <Stat label="Vehicle code" value={<span className="font-mono text-[#FFD60A]" data-testid={`fleet-detail-code-${v.vehicle_id}`}>{v.vehicle_code}</span>} />
        )}
        <Stat label="Assigned" value={v.assignment?.name || v.driver_name || 'Unassigned'} />
        <Stat label="Mileage" value={`${(v.current_mileage || 0).toLocaleString()} mi`} />
        <Stat label="Next service" value={`${nextMarker.toLocaleString()} mi`} accent={flag.active ? '#f87171' : undefined} />
        <Stat label="VIN" value={<span className="font-mono text-[11px]">{v.vin || '—'}</span>} />
        <Stat label="Type" value={v.vehicle_type || '—'} />
        <Stat label="Color" value={v.color || '—'} />
        <Stat label="Gas card" value={gasCard ? <span className="font-mono text-[11px]" data-testid={`fleet-gas-card-${v.vehicle_id}`}>{gasCard.masked}</span> : '—'} />
      </div>

      {/* Maintenance flag + optional receipt-upload */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {flag.active ? (
          <Badge className="bg-red-600 text-white" data-testid={`fleet-maint-badge-${v.vehicle_id}`}>
            <AlertTriangle className="h-3 w-3 mr-1" /> MAINTENANCE DUE at {flag.mileage_at_flag?.toLocaleString?.()} mi
          </Badge>
        ) : (
          <Badge className="bg-emerald-700 text-white">Maintenance: clear</Badge>
        )}
        {v.mpg_anomaly && (
          <Badge className="bg-orange-600 text-white" data-testid={`fleet-mpg-badge-${v.vehicle_id}`}>
            <AlertTriangle className="h-3 w-3 mr-1" /> MPG anomaly (latest {v.latest_mpg ?? '—'})
          </Badge>
        )}
        {tempActive && (
          <Badge className="bg-amber-500 text-black" data-testid={`fleet-temp-badge-${v.vehicle_id}`}>
            Temp: {v.temp_assignment.name} · until {v.temp_assignment.end_date}
          </Badge>
        )}
      </div>

      {/* Maintenance receipt (own vehicle · flag active) */}
      {isMine && flag.active && (
        <div className="rounded-lg border p-3" style={{ borderColor: 'rgba(248,113,113,0.5)', backgroundColor: 'rgba(127,29,29,0.25)' }} data-testid={`maint-receipt-block-${v.vehicle_id}`}>
          <p className="text-sm font-bold text-white mb-2">File 5,000-mile service receipt</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-zinc-400">Receipt (image/PDF ≤4MB)</Label>
              <Input type="file" accept="image/*,application/pdf" onChange={onFile((u) => setReceipt((r) => ({ ...r, url: u })))} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`maint-file-${v.vehicle_id}`} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Amount (USD)</Label>
              <Input type="number" step="0.01" value={receipt.amount} onChange={(e) => setReceipt((r) => ({ ...r, amount: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Note</Label>
              <Textarea rows={2} value={receipt.note} onChange={(e) => setReceipt((r) => ({ ...r, note: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
          </div>
          <Button onClick={submitReceipt} disabled={busy || !receipt.url} className="mt-2 bg-red-600 hover:bg-red-500 text-white font-bold" data-testid={`maint-submit-${v.vehicle_id}`}>
            <Upload className="h-4 w-4 mr-2" /> {busy ? 'Filing…' : 'File Receipt & Clear Flag'}
          </Button>
        </div>
      )}

      {/* Daily inspection (own vehicle · operation days only) */}
      {isMine && isOpDay && (
        <div className="rounded-lg border border-zinc-700 p-3 bg-zinc-950/60" data-testid={`inspection-block-${v.vehicle_id}`}>
          <p className="text-sm font-bold text-white flex items-center mb-2">
            <ClipboardCheck className="h-4 w-4 mr-2" style={{ color: '#FFD60A' }} /> Daily Vehicle Inspection
          </p>
          <div className="grid sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-zinc-400">Mileage</Label>
              <Input type="number" value={inspection.mileage} onChange={(e) => setInspection((s) => ({ ...s, mileage: e.target.value }))} placeholder={`${(v.current_mileage || 0).toLocaleString()}`} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`inspection-mileage-${v.vehicle_id}`} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Condition notes</Label>
              <Input value={inspection.notes} onChange={(e) => setInspection((s) => ({ ...s, notes: e.target.value }))} placeholder="Tires ok, oil ok, no leaks" className="bg-zinc-800 border-zinc-700 text-white" data-testid={`inspection-notes-${v.vehicle_id}`} />
            </div>
          </div>
          <Button onClick={submitInspection} disabled={busy} className="mt-2 bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold" data-testid={`inspection-submit-${v.vehicle_id}`}>
            <CheckCircle2 className="h-4 w-4 mr-2" /> {busy ? 'Filing…' : 'File Inspection'}
          </Button>
        </div>
      )}
      {isMine && !isOpDay && (
        <p className="text-[11px] text-zinc-500" data-testid={`non-op-day-${v.vehicle_id}`}>
          Non-operation day (Sunday / holiday) — inspection form is hidden.
        </p>
      )}

      {/* Fuel receipt (own vehicle) */}
      {isMine && gasCard && (
        <div className="rounded-lg border border-zinc-700 p-3 bg-zinc-950/60" data-testid={`fuel-block-${v.vehicle_id}`}>
          <p className="text-sm font-bold text-white flex items-center mb-2">
            <Fuel className="h-4 w-4 mr-2" style={{ color: '#22c55e' }} /> Fuel Receipt · {gasCard.masked}
          </p>
          <div className="grid sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-zinc-400">Date</Label>
              <Input type="date" value={fuel.date} onChange={(e) => setFuel((f) => ({ ...f, date: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`fuel-date-${v.vehicle_id}`} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Gas station</Label>
              <Input value={fuel.station} onChange={(e) => setFuel((f) => ({ ...f, station: e.target.value }))} placeholder="Chevron on Preston" className="bg-zinc-800 border-zinc-700 text-white" data-testid={`fuel-station-${v.vehicle_id}`} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Gallons</Label>
              <Input type="number" step="0.01" value={fuel.gallons} onChange={(e) => setFuel((f) => ({ ...f, gallons: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`fuel-gallons-${v.vehicle_id}`} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Total (USD)</Label>
              <Input type="number" step="0.01" value={fuel.total_amount} onChange={(e) => setFuel((f) => ({ ...f, total_amount: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`fuel-total-${v.vehicle_id}`} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Receipt file</Label>
              <Input type="file" accept="image/*,application/pdf" onChange={onFile((u) => setFuel((f) => ({ ...f, receipt_url: u })))} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`fuel-file-${v.vehicle_id}`} />
            </div>
          </div>
          <Button onClick={submitFuel} disabled={busy} className="mt-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold" data-testid={`fuel-submit-${v.vehicle_id}`}>
            <Upload className="h-4 w-4 mr-2" /> {busy ? 'Filing…' : 'File Fuel Receipt'}
          </Button>
        </div>
      )}

      {/* Temp assignment panel (RM only) */}
      {isRM && (
        <div className="rounded-lg border border-amber-500/40 p-3 bg-amber-500/5" data-testid={`temp-assign-block-${v.vehicle_id}`}>
          <p className="text-sm font-bold text-white flex items-center mb-2">
            <UserPlus className="h-4 w-4 mr-2" style={{ color: '#f59e0b' }} /> Temporary Assignment (max 7 days)
          </p>
          {tempActive ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-zinc-200">
                <b>{v.temp_assignment.name}</b> is temporarily assigned from{' '}
                <span className="text-amber-300">{v.temp_assignment.start_date}</span> to{' '}
                <span className="text-amber-300">{v.temp_assignment.end_date}</span>.
              </p>
              <Button size="sm" onClick={terminateTempAssign} disabled={busy} className="bg-zinc-800 border border-zinc-600 text-red-300 hover:text-red-100" data-testid={`temp-terminate-${v.vehicle_id}`}>
                <Ban className="h-3 w-3 mr-1" /> Terminate
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Replacement (Supervisor / Crew Leader)</Label>
                <select
                  value={tempForm.user_id}
                  onChange={(e) => setTempForm((f) => ({ ...f, user_id: e.target.value }))}
                  className="h-9 w-full px-2 rounded bg-zinc-800 border border-zinc-700 text-white text-xs"
                  data-testid={`temp-user-${v.vehicle_id}`}
                >
                  <option value="">— Pick staff —</option>
                  {replacements.map((r) => (
                    <option key={r.user_id} value={r.user_id}>{r.name} · {r.role?.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Start</Label>
                <Input type="date" value={tempForm.start_date} onChange={(e) => setTempForm((f) => ({ ...f, start_date: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`temp-start-${v.vehicle_id}`} />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Days (max 7)</Label>
                <Input type="number" min={1} max={7} value={tempForm.days} onChange={(e) => setTempForm((f) => ({ ...f, days: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`temp-days-${v.vehicle_id}`} />
              </div>
              <div className="sm:col-span-3">
                <Button onClick={submitTempAssign} disabled={busy || !tempForm.user_id} className="bg-amber-500 text-black hover:bg-amber-400 font-bold" data-testid={`temp-submit-${v.vehicle_id}`}>
                  <UserPlus className="h-4 w-4 mr-2" /> {busy ? 'Assigning…' : 'Assign Temporarily'}
                </Button>
              </div>
            </div>
          )}
          {replacements.length === 0 && !tempActive && (
            <p className="text-[11px] text-zinc-500 mt-1">No eligible replacement staff on file (Supervisor / Crew Leader with driver's license).</p>
          )}
        </div>
      )}

      {/* MPG trend (always) */}
      <VehicleMpgChart testId={`fleet-mpg-${v.vehicle_id}`} vehicleId={v.vehicle_id} hidePicker />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="text-sm font-semibold" style={{ color: accent || '#ffffff' }}>{value}</p>
    </div>
  );
}
